import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Ban } from 'lucide-react'
import { useGHLFollowUpTimeline, useCancelFollowUp, useCancelAllFollowUps, useGHLContactNames } from '@/hooks/useGHLDashboard'
import {
  type GHLFollowUpRow,
  formatFireAt,
  getFollowUpStatusColor,
  formatStepBadge,
  truncateId,
} from '@/lib/ghl-dashboard'
import { ContactAvatar } from './ContactAvatar'

interface Props {
  readonly opportunityId: string
}

const STEP_LABELS: Record<string, string> = {
  '1h_after_quote': '1 hour after quote',
  '1d_after_quote': '1 day after quote',
  '2d_after_quote': '2 days after quote',
  '3d_after_quote': '3 days after quote',
  '7d_after_quote': '7 days after quote',
  '14d_after_quote': '14 days after quote',
  '30d_after_quote': '30 days after quote',
  '60d_after_quote': '60 days after quote',
  '90d_after_quote': '90 days after quote',
  'mark_lost': 'Mark as lost',
}

function StepDot({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    sent: 'bg-green-500',
    pending: 'bg-blue-400',
    processing: 'bg-yellow-500 animate-pulse',
    failed: 'bg-red-500',
    cancelled: 'bg-gray-400',
    skipped: 'bg-amber-400',
  }

  return (
    <div className={`w-3 h-3 rounded-full ${colorMap[status] ?? 'bg-gray-300'} ring-2 ring-background`} />
  )
}

function TimelineStep({ step, isLast, onCancel, isCancelling }: {
  step: GHLFollowUpRow
  isLast: boolean
  onCancel: (step: GHLFollowUpRow) => void
  isCancelling: boolean
}) {
  const statusColor = getFollowUpStatusColor(step.status)
  const isPending = step.status === 'pending'
  const isFuture = new Date(step.fire_at).getTime() > Date.now()

  return (
    <div className="flex gap-3">
      {/* Vertical line + dot */}
      <div className="flex flex-col items-center">
        <StepDot status={step.status} />
        {!isLast && (
          <div className="w-px flex-1 bg-border min-h-[24px]" />
        )}
      </div>

      {/* Content */}
      <div className={`pb-4 ${isPending && isFuture ? 'opacity-50' : ''}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            Step {step.step_number}: {STEP_LABELS[step.step_label] ?? step.step_label}
          </span>
          <Badge className={`${statusColor.bg} ${statusColor.text} text-[10px]`}>
            {statusColor.label}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatFireAt(step.fire_at)}
          </span>
          {isPending && (
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-red-600"
              disabled={isCancelling}
              onClick={() => onCancel(step)}
            >
              Cancel
            </Button>
          )}
        </div>

        {/* SMS message (if sent) */}
        {step.sms_message && (
          <p className="text-sm text-muted-foreground mt-1 pl-0.5 border-l-2 border-muted ml-0 pl-2 max-w-lg">
            {step.sms_message}
          </p>
        )}

        {/* Skip/cancel reason */}
        {step.skip_reason && (
          <p className="text-xs text-muted-foreground/60 italic mt-1">
            {step.skip_reason}
          </p>
        )}

        {/* Error */}
        {step.error_message && (
          <p className="text-xs text-red-500 mt-1">
            Error: {step.error_message}
          </p>
        )}

        {/* Retry info */}
        {step.attempt_count > 1 && step.status === 'failed' && (
          <p className="text-[10px] text-muted-foreground/50 mt-0.5">
            {step.attempt_count} attempts
          </p>
        )}
      </div>
    </div>
  )
}

type CancelMode =
  | { type: 'single'; step: GHLFollowUpRow }
  | { type: 'all'; opportunityId: string; count: number }

export function FollowUpTimeline({ opportunityId }: Props) {
  const { data: steps, isLoading } = useGHLFollowUpTimeline(opportunityId)
  const cancelOne = useCancelFollowUp()
  const cancelAll = useCancelAllFollowUps()
  const [cancelMode, setCancelMode] = useState<CancelMode | null>(null)

  const pendingCount = steps?.filter(s => s.status === 'pending').length ?? 0
  const isCancelling = cancelOne.isPending || cancelAll.isPending

  // Every step belongs to the same contact/location, so we pick the first one
  const firstStep = steps?.[0]
  const contactIds = useMemo(
    () => (firstStep?.contact_id ? [firstStep.contact_id] : []),
    [firstStep?.contact_id]
  )
  const { data: contactNames } = useGHLContactNames(contactIds, firstStep?.location_id)
  const contact = firstStep?.contact_id ? contactNames?.get(firstStep.contact_id) : undefined

  function handleConfirm() {
    if (!cancelMode) return
    if (cancelMode.type === 'single') {
      cancelOne.mutate({ id: cancelMode.step.id }, {
        onSettled: () => setCancelMode(null),
      })
    } else {
      cancelAll.mutate({ opportunityId: cancelMode.opportunityId }, {
        onSettled: () => setCancelMode(null),
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3 py-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-3 items-start">
            <Skeleton className="w-3 h-3 rounded-full" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-72" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!steps || steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        No follow-up steps found for this opportunity.
      </p>
    )
  }

  return (
    <div>
      {/* Contact header — primary anchor for cross-referencing with GHL */}
      {firstStep && (
        <div className="mb-4 pb-4 border-b">
          <ContactAvatar
            size="md"
            showName
            firstName={contact?.firstName}
            lastName={contact?.lastName}
            fullName={contact?.fullName}
            contactId={firstStep.contact_id}
            subtitle={contact?.email ?? undefined}
          />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs font-medium text-muted-foreground">
          Timeline for opportunity {truncateId(opportunityId, 12)}
        </p>
        <Badge variant="outline" className="text-[10px]">
          {steps.filter(s => s.status === 'sent').length}/{steps.length} sent
        </Badge>
        {pendingCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
            disabled={isCancelling}
            onClick={() => setCancelMode({ type: 'all', opportunityId, count: pendingCount })}
          >
            <Ban className="h-3 w-3 mr-1" />
            Cancel All Pending ({pendingCount})
          </Button>
        )}
      </div>
      <div className="pl-1">
        {steps.map((step, idx) => (
          <TimelineStep
            key={step.id}
            step={step}
            isLast={idx === steps.length - 1}
            onCancel={(s) => setCancelMode({ type: 'single', step: s })}
            isCancelling={isCancelling}
          />
        ))}
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancelMode} onOpenChange={(open) => { if (!open) setCancelMode(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {cancelMode?.type === 'all'
                ? `Cancel all pending follow-ups?`
                : `Cancel scheduled SMS?`
              }
            </AlertDialogTitle>
            <AlertDialogDescription>
              {cancelMode?.type === 'all' ? (
                <>
                  This will cancel <span className="font-medium text-foreground">{cancelMode.count} pending</span> follow-up
                  {cancelMode.count > 1 ? ' messages' : ' message'} for this opportunity. Already sent messages are not affected.
                  This action cannot be undone.
                </>
              ) : cancelMode?.type === 'single' ? (
                <>
                  This will cancel the follow-up SMS for step{' '}
                  <span className="font-medium text-foreground">
                    {formatStepBadge(cancelMode.step.step_number, cancelMode.step.step_label)}
                  </span>{' '}
                  scheduled for{' '}
                  <span className="font-medium text-foreground">
                    {formatFireAt(cancelMode.step.fire_at)}
                  </span>.
                  This action cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={isCancelling}
              onClick={handleConfirm}
            >
              {isCancelling ? 'Cancelling...' : cancelMode?.type === 'all' ? `Cancel ${cancelMode.count} messages` : 'Yes, cancel it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
